import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, Sparkles, Flame } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

function fmtItDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function PricingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(null);
    const [plans, setPlans] = useState(null);

    useEffect(() => {
        api.get("/pricing/public")
            .then(({ data }) => setPlans(data.plans))
            .catch(() => setPlans({
                base: { price: 9, current_price: 9, promo_active: false },
                pro: { price: 19, current_price: 19, promo_active: false },
            }));
    }, []);

    const subscribe = async (plan) => {
        if (!user) {
            navigate("/register");
            return;
        }
        if (!user.has_billing_info) {
            navigate("/billing/info", { state: { next: "/pricing" } });
            return;
        }
        setBusy(plan);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            const status = e.response?.status;
            if (status === 412) {
                // Backend says billing info is missing - send user to billing form
                navigate("/billing/info", { state: { next: "/pricing" } });
                return;
            }
            toast.error(formatApiError(e.response?.data?.detail));
            setBusy(null);
        }
    };

    return (
        <div className="min-h-screen bg-sf-bg" data-testid="pricing-page">
            <header className="glass-nav sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
                    <Link to={user ? "/app" : "/"}>
                        <Logo className="h-28 w-auto" />
                    </Link>
                    <Link to={user ? "/app" : "/"}>
                        <Button variant="ghost" data-testid="pricing-back">
                            <ArrowLeft size={16} className="mr-2" /> Indietro
                        </Button>
                    </Link>
                </div>
            </header>
            <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
                <div className="text-center mb-16">
                    <div className="label-eyebrow mb-3">Piani</div>
                    <h1 className="font-display text-5xl md:text-6xl text-navy mb-4">
                        Scegli il tuo
                        <span className="italic text-amber-brand"> Flow</span>.
                    </h1>
                    <p className="text-sf-secondary text-lg max-w-2xl mx-auto">
                        Cancella quando vuoi. Pagamenti sicuri via Stripe.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <Card
                        plan="base"
                        name="Base"
                        info={plans?.base}
                        active={user?.subscription_tier === "base"}
                        items={[
                            "Fino a 10 dipendenti",
                            "Calendario turni settimanale",
                            "Anagrafica brigata completa",
                            "Performance base (ore, presenze, puntualità)",
                            "Report base",
                            "Email support",
                        ]}
                        onSubscribe={subscribe}
                        busy={busy}
                    />
                    <Card
                        plan="pro"
                        featured
                        name="Pro"
                        info={plans?.pro}
                        active={user?.subscription_tier === "pro"}
                        items={[
                            "Dipendenti illimitati",
                            "Performance avanzate (coperti, scontrino medio, mance, rating)",
                            "Brigata productivity analytics",
                            "Grafici e ranking",
                            "Multi-locale",
                            "Notifiche email",
                            "Support prioritario",
                        ]}
                        onSubscribe={subscribe}
                        busy={busy}
                    />
                </div>
                <div className="text-center text-xs text-sf-muted mt-12">
                    Staff Flow 360 è un prodotto di Makai · P.IVA 09364410969
                </div>
            </section>
        </div>
    );
}

function Card({ plan, name, info, items, featured, active, onSubscribe, busy }) {
    const promoActive = !!info?.promo_active;
    const basePrice = info?.price ?? (plan === "pro" ? 19 : 9);
    const currentPrice = info?.current_price ?? basePrice;

    return (
        <div
            className={`relative rounded-2xl p-8 lift ${
                featured
                    ? "bg-navy text-white border-2 border-amber-brand"
                    : "bg-white border border-sf"
            }`}
            data-testid={`pricing-card-${plan}`}
        >
            {featured && (
                <div className="absolute -top-3 left-8 bg-amber-brand text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
                    <Sparkles size={11} /> Più scelto
                </div>
            )}
            {promoActive && (
                <div
                    className="absolute -top-3 right-8 bg-[#d90429] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1"
                    data-testid={`pricing-${plan}-promo-badge`}
                >
                    <Flame size={11} /> Promo
                </div>
            )}
            <div className={`label-eyebrow mb-2 ${featured ? "text-amber-brand" : ""}`}>
                Piano
            </div>
            <div className="font-display text-3xl mb-6">{name}</div>

            {promoActive ? (
                <div className="mb-3">
                    <div className="flex items-baseline gap-3">
                        <span className="font-display text-6xl">€{currentPrice.toFixed(currentPrice % 1 === 0 ? 0 : 2)}</span>
                        <span className={featured ? "text-white/70" : "text-sf-muted"}>
                            /mese
                        </span>
                    </div>
                    <div
                        className={`mt-1 ${featured ? "text-white/60" : "text-sf-muted"}`}
                        data-testid={`pricing-${plan}-original-price`}
                    >
                        <span className="line-through text-lg">
                            €{basePrice.toFixed(basePrice % 1 === 0 ? 0 : 2)}/mese
                        </span>
                    </div>
                </div>
            ) : (
                <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-display text-6xl">€{basePrice.toFixed(basePrice % 1 === 0 ? 0 : 2)}</span>
                    <span className={featured ? "text-white/70" : "text-sf-muted"}>
                        /mese
                    </span>
                </div>
            )}

            {promoActive && info?.promo_ends_at && (
                <div
                    className={`mb-6 text-sm font-semibold ${featured ? "text-amber-brand" : "text-[#d90429]"}`}
                    data-testid={`pricing-${plan}-promo-until`}
                >
                    Offerta valida fino al {fmtItDate(info.promo_ends_at)}
                </div>
            )}
            {!promoActive && <div className="mb-6" />}

            <ul className="space-y-3 mb-8 text-sm">
                {items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                        <Check
                            size={16}
                            className={`mt-0.5 ${featured ? "text-amber-brand" : "text-navy"}`}
                        />
                        <span className={featured ? "text-white/90" : "text-sf-secondary"}>
                            {it}
                        </span>
                    </li>
                ))}
            </ul>
            {active ? (
                <Button
                    className="w-full"
                    variant="outline"
                    disabled
                    data-testid={`pricing-${plan}-current`}
                >
                    Piano attivo
                </Button>
            ) : (
                <Button
                    className={`w-full lift ${
                        featured
                            ? "bg-amber-brand text-navy hover:bg-amber-brand/90 font-bold"
                            : "bg-navy text-white hover:bg-navy/90"
                    }`}
                    onClick={() => onSubscribe(plan)}
                    disabled={busy === plan}
                    data-testid={`pricing-${plan}-subscribe`}
                >
                    {busy === plan
                        ? "Reindirizzamento…"
                        : `Sottoscrivi €${currentPrice.toFixed(currentPrice % 1 === 0 ? 0 : 2)}/mese`}
                </Button>
            )}
        </div>
    );
}
